import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Banner } from '@els/els-react--banner';
import { Button } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import { Modal } from '@els/els-react--modal';
import { Dropdown } from '@els/els-react--dropdown';
import { ValidatedTextInput } from '@els/els-react--text-input';
import { Loader } from '@els/els-react--loader';
import take from 'lodash/take';
import toArray from 'lodash/toArray';
import { StructureFromNameOptions, StructureFromNameTexts } from '@Pages/StructureEditor/StructureEditorConstants';
import { STRUCTURE_INSERT_MODE_LOAD } from '@Pages/StructureEditor/constants';
import {
  MODAL_SPINNER_ID,
  MIN_QUERY_LENGTH,
  COUNT_SHOW_SUBSTANCES,
} from '@Pages/StructureEditor/Components/StructureFromNameModalConstants';
import StructureFromName from '@Services/StructureFromName';
import Catalyst from '@Services/Catalyst';
import { trackButton } from '@Pages/StructureEditor/analytics';

function StructureFromNameModal({ closeModal, addStructureToEditor }) {
  const modalQueryOptions = toArray(StructureFromNameOptions).map((option) => ({
    label: option.viewValue,
    value: option.serverValue,
  }));
  const [modalErrorText, setModalErrorText] = useState('');
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [modalQuery, setModalQuery] = useState('');
  const [selectQueryOption, setSelectQueryOption] = useState(
    modalQueryOptions.find((option) => option.value === StructureFromNameOptions?.IS?.serverValue) || modalQueryOptions[0]
  );
  const [allSubstancesModal, setAllSubstancesModal] = useState(null);
  const [modalSubstances, setModalSubstances] = useState(null);
  const fromNameAllSubstancesRef = useRef(null);
  const queryInputRef = useRef(null);
  const queryValidatorRef = useRef(null);
  const hadModalErrorRef = useRef(false);

  function structureFromNameSearch(structureFromName, structureNameFromName) {
    addStructureToEditor(structureFromName, structureNameFromName, STRUCTURE_INSERT_MODE_LOAD);
    trackButton('submit structure from name');
    closeModal();
  }

  function modalStructureSearch() {
    const value = modalQuery;
    setModalErrorText('');
    Logger.info(`Structure search: ${selectQueryOption?.value} ${value}`);
    if (value && value.length >= MIN_QUERY_LENGTH && selectQueryOption?.value) {
      setIsModalLoading(true);

      StructureFromName.getMolForStructure(value, selectQueryOption.value)
        .then((response) => {
          if (Array.isArray(response.substances)) {
            const allSubstances = response.substances;
            setAllSubstancesModal(allSubstances);

            if (allSubstances.length === 1) {
              structureFromNameSearch(allSubstances[0]?.structure, value);
            } else {
              setModalSubstances(allSubstances.slice(0, COUNT_SHOW_SUBSTANCES));
            }
          } else {
            setModalErrorText(StructureFromNameTexts?.nameNotFoundErrorMessage);
            Catalyst.trackError({
              component: 'Structure from name',
            });
          }
        })
        .catch(Logger.error)
        .finally(() => {
          Logger.info('Terminate spinner');
          setIsModalLoading(false);
        });
    }
  }

  const handleSubmit = useCallback(() => {
    modalStructureSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalQuery, selectQueryOption, modalSubstances, modalErrorText]);

  const validateModalQuery = useCallback(() => (modalErrorText ? [{ isValid: false, message: modalErrorText }] : null), [modalErrorText]);

  useEffect(() => {
    if (modalErrorText || hadModalErrorRef.current) {
      queryValidatorRef.current?.validate(modalQuery, true);
      hadModalErrorRef.current = !!modalErrorText;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalErrorText]);

  useEffect(() => {
    const handleScroll = () => {
      const container = fromNameAllSubstancesRef.current;
      if (container.scrollTop + container.clientHeight >= container.scrollHeight) {
        if (modalSubstances < allSubstancesModal) {
          setModalSubstances(take(allSubstancesModal, modalSubstances.length + COUNT_SHOW_SUBSTANCES));
        }
      }
    };

    const container = fromNameAllSubstancesRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [modalSubstances, allSubstancesModal]);

  useLayoutEffect(() => {
    const focusInput = requestAnimationFrame(() => {
      queryInputRef.current?.focus();
    });

    return () => cancelAnimationFrame(focusInput);
  }, []);

  const handleInputKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <Modal
      id="structure-from-name-modal"
      className="insert-from-name-modal"
      label="Create structure template from name"
      onClose={() => closeModal()}
      width="50rem">
      <div className="insert-from-name-modal__body">
        <h3 id="dialog-title" className="insert-from-name-modal__title">
          Create structure template from name
        </h3>
        <div className="insert-from-name-modal__search-row">
          <Dropdown
            id="structureFromName-options"
            className="structure-from-name-operator-dropdown"
            label="Select option"
            name="structureFromNameOption"
            options={modalQueryOptions}
            selected={selectQueryOption}
            onChange={setSelectQueryOption}
            showSelectedAsLabel
            buttonProps={{
              type: 'link',
'aria-label': `Select structure from name option, current operator: ${selectQueryOption?.label}`,
              'data-e2e': 'insert-structure-from-name-dialog-option-dropdown',
            }}
          />
          <div className="insert-from-name-modal__query-field">
            <ValidatedTextInput
              data-e2e="search-structure"
              id="structureFromName-query"
              inputRef={queryInputRef}
              ref={queryValidatorRef}
              value={modalQuery}
              onChange={(e) => setModalQuery(e.target?.value)}
              onKeyDown={handleInputKeyDown}
              shouldHideLabel
              aria-describedby={modalErrorText ? 'structureFromName-query-message-inner structureFromName-banner' : undefined}
              label="Enter a chemical name, CAS-RN, InChiKey or SMILES"
              validator={validateModalQuery}
              onlyValidateWhenTouched={false}
              iconRight={{
                sprite: 'Search',
                id: 'search-icon',
                children: 'Search for structure',
                a11y: {
                  name: 'Search for structure from name',
                },
              }}
              iconRightCallback={() => modalStructureSearch()}
            />
            {modalErrorText && (
              <span className="u-els-hide-visually" role="alert">
                {modalErrorText}
              </span>
            )}
          </div>
        </div>
        {modalErrorText && (
          <Banner
            id="structureFromName-banner"
            className="insert-from-name-modal__banner"
            type="info"
            size="small"
            isVisible
            isIconVisible
            hideCloseLabel>
            {StructureFromNameTexts.nameNotFoundInfoMessage}
          </Banner>
        )}

        {isModalLoading && (
          <Loader id={MODAL_SPINNER_ID} isVisible isBlocking={false} isSmall messages={{ LOADING_MESSAGE: 'Loading structure' }} />
        )}

        <div className="substances-container">
          <div ref={fromNameAllSubstancesRef} className="substances">
            {modalSubstances?.map((substance) => {
              const substanceName = substance?.substanceNames?.[0]?.value;
              const altText = substanceName ? `chemical structure of ${substanceName}` : 'chemical structure';
              return (
                <Card key={substance?.rxId} className="u-els-margin-bottom-1x u-els-margin-right-1x">
                  <div className="o-els-flex-layout o-els-flex-layout--middle">
                    <div className="o-els-flex-layout__item substance-card-item">
                      <img src={substance?.path} alt={altText} draggable="false" />
                    </div>
                    <div className="o-els-flex-layout__item--grow substance-card-item">
                      <div>{substance?.substanceNames?.[0]?.value}</div>
                      {substance?.referencesNumber && <div>{substance?.referencesNumber} references</div>}
                    </div>
                    <div className="o-els-flex-layout__item substance-card-item substance-button-container">
                      <Button
                        data-e2e="load-template-button"
                        id={`button-${substance?.rxId}`}
                        type="secondary"
                        onClick={() => structureFromNameSearch(substance?.structure, substance?.substanceNames?.[0]?.value)}>
                        Load template
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}

StructureFromNameModal.propTypes = {
  closeModal: PropTypes.func,
  addStructureToEditor: PropTypes.func,
};

export default StructureFromNameModal;
